import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Button, SvgIcon } from '@mui/material';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import parse, { Element } from 'html-react-parser';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/header/header';
import LoadingSpinner from '../../components/loadingSpinner/loadingSpinner';
import { ReactComponent as unreadIcon } from '../../assets/tab_error.svg';
import { AuthContext } from '../../services/authContextProvider';
import ObrasAPI from '../../services/APIObras';
import { NotificationContext } from '../../context/NotificationProvider';
import { Motivo, NotificationType } from '../../types/Notification';
import styles from './allNotifications.module.scss';

const scopeMotives = new Set<Motivo>([
  Motivo.NEW_SUPERCENTER_EDITOR,
  Motivo.NEW_SUPERCENTER_VALIDATOR,
  Motivo.NEW_ZONE_EDITOR,
  Motivo.NEW_ZONE_VALIDATOR,
]);

export default function AllNotifications() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const auth = useContext(AuthContext);
  const newNotification = useContext(NotificationContext);
  const api = ObrasAPI.getInstance();
  const isAuthorized = auth.canSystem('can_save_any_project') || auth.canSystem('can_validate_any_project');
  const [items, setItems] = useState<NotificationType[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const requestRef = useRef<AbortController | null>(null);

  const load = useCallback(async (nextCursor?: string, controller = new AbortController()) => {
    requestRef.current?.abort();
    requestRef.current = controller;
    setLoading(true);
    setError(false);
    try {
      const page = await api.getNotificationsAPI(undefined, nextCursor, controller.signal);
      if (controller.signal.aborted) return;
      setItems(previous => nextCursor
        ? [...new Map([...previous, ...page.data].map(item => [item.id, item])).values()]
        : page.data);
      setCursor(page.nextCursor);
      setHasNextPage(page.hasNextPage);
    } catch {
      if (!controller.signal.aborted) setError(true);
    } finally {
      if (!controller.signal.aborted) setLoading(false);
      if (requestRef.current === controller) requestRef.current = null;
    }
  }, [api]);

  useEffect(() => {
    if (!isAuthorized) return;
    const controller = new AbortController();
    void load(undefined, controller);
    return () => controller.abort();
  }, [isAuthorized, load, newNotification]);

  const openNotification = async (item: NotificationType) => {
    if (!item.leido) {
      const result = await api.setNotificationRead(item);
      if (result.statusCode === 200) {
        setItems(previous => previous.map(current => current.id === item.id ? { ...current, leido: true } : current));
      }
    }
    navigate(scopeMotives.has(item.motivo) ? '/acciones-pendientes'
      : item.motivo === Motivo.REJECT ? '/acciones-pendientes?filter=COMMENTED'
        : `/${item.codCentro}/detalles`);
  };

  const formatDate = (value: string) => new Date(value).toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });

  return <div className={styles.page}>
    <Header>
      <div className={styles.heading}>
        <button className={styles.back} onClick={() => navigate(-1)} aria-label={String(t('notifications.back'))}>
          <ArrowBackIosIcon />
        </button>
        {t('notifications.title')}
      </div>
    </Header>
    <main className={styles.content}>
      {items.map(item => <button key={item.id} className={styles.item} onClick={() => void openNotification(item)}>
        <span className={styles.point}>{!item.leido && <SvgIcon component={unreadIcon} inheritViewBox />}</span>
        <span className={styles.details}>
          <strong>{item.nomCentro || item.codCentro}{item.nomCentro && item.codCentro ? ` - ${item.codCentro}` : ''}</strong>
          <span>{parse(t(`notifications.${item.motivo}`), {
            replace: node => node instanceof Element && node.attribs?.id === 'replace'
              ? <span>{item.nomCentro}</span> : undefined,
          })}</span>
          <time dateTime={item.fecCreacion}>{formatDate(item.fecCreacion)}</time>
        </span>
      </button>)}
      {!loading && !error && items.length === 0 && <p>{t('notifications.noData')}</p>}
      {loading && <LoadingSpinner />}
      {error && <div role="alert">
        <p>{t('notifications.loadError')}</p>
        <Button onClick={() => void load(cursor || undefined)}>{t('notifications.retry')}</Button>
      </div>}
      {!loading && !error && hasNextPage && cursor &&
        <Button onClick={() => void load(cursor)}>{t('notifications.loadMore')}</Button>}
    </main>
  </div>;
}
